import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatLocalizedDate, parseDateValue } from '@/lib/date-utils';
import { DealState } from '@/lib/graphql/generated/graphql';
import { differenceInCalendarDays } from 'date-fns';
import { CalendarDays, Sparkles } from 'lucide-react';

import {
  DealStatusBadge,
  getDealListStatus,
  type DealListRow,
} from './deal-status-badge';

export function DealRow({
  deal,
  onGenerate,
}: {
  deal: DealListRow;
  onGenerate: (dealId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const status = getDealListStatus(deal);
  const date = parseDateValue(deal.startDate);
  const days = date ? differenceInCalendarDays(date, new Date()) : null;
  const managerName =
    deal.manager.name === 'Unknown'
      ? t('deals-list.unknown')
      : deal.manager.name;
  const url = `/deal/${encodeURIComponent(deal.id)}`;
  const destination =
    status === 'notStarted'
      ? `${url}/start`
      : status === 'csv'
        ? `${url}/csv`
        : url;
  return (
    <TableRow className="relative cursor-pointer">
      <TableCell className="w-[58%] px-5 py-4">
        <Link
          to={destination}
          className="font-semibold after:absolute after:inset-0 focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring"
        >
          {deal.dealName}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <CalendarDays size={14} />
          <span>{date ? formatLocalizedDate(date, i18n.language) : '—'}</span>
          {days !== null && (
            <span>
              ·{' '}
              {t(days >= 0 ? 'deals-list.inDays' : 'deals-list.daysAgo', {
                count: Math.abs(days),
              })}
            </span>
          )}
          <Avatar className="ml-1 h-5 w-5 text-[9px]">
            <AvatarImage src={deal.manager.photoUrl ?? undefined} alt="" />
            <AvatarFallback>
              {managerName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span>{managerName}</span>
        </div>
      </TableCell>
      <TableCell className="px-5">
        <DealStatusBadge deal={deal} />
      </TableCell>
      <TableCell className="px-5">
        <div className="flex justify-end gap-2">
          {deal.state === DealState.Empty && (
            <Button
              variant="outline"
              className="relative z-10"
              onClick={() => onGenerate(deal.id)}
            >
              <Sparkles />
              {t('deals-list.actions.generate')}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
